import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { RESCUE_STATUS_LABEL_KEYS } from "../constants/payoutRescue.constants";
import type { PaymentStatusChange } from "../services/payoutRescue.service";
import { shortId } from "../utils/payoutRescueFormat";

export interface RescueTimelineProps {
  /** Real transition log (`payment_status_changes`, seq ASC) — INTERNAL-only endpoint. */
  timeline: PaymentStatusChange[] | null;
  isLoading: boolean;
  hasError: boolean;
  /** Actor_user_id → resolved display name/email; unresolved ids render shortened. */
  actorLabels: Record<string, string>;
  /** True while the payout can still move — the last step pulses as "live". */
  isNonTerminal: boolean;
}

const STATUS_DOT_CLASS: Record<string, string> = {
  CREATED: "bg-warning",
  PROCESSING: "bg-blue-500",
  PAID: "bg-success",
  FAILED: "bg-destructive",
  CANCELLED: "bg-muted-foreground",
  REVERSED: "bg-destructive",
};

/**
 * Audit timeline of the drawer — answers "who retried this and when" for compliance. Each step is a real
 * recorded transition with its source (staff / banking network / system — provider identity is NEVER
 * rendered), the resolved acting user and the reason on failures/cancellations.
 */
export function RescueTimeline({
  timeline,
  isLoading,
  hasError,
  actorLabels,
  isNonTerminal,
}: RescueTimelineProps) {
  const { t } = useTranslations("payoutRescue");

  if (isLoading) {
    return (
      <div className="space-y-2" aria-hidden="true">
        <ColocaSkeleton className="h-4 w-2/3" />
        <ColocaSkeleton className="h-4 w-1/2" />
        <ColocaSkeleton className="h-4 w-3/5" />
      </div>
    );
  }

  if (hasError || !timeline || timeline.length === 0) {
    return <p className="text-xs text-muted-foreground">{t("timeline_error")}</p>;
  }

  const sourceLabel: Record<string, string> = {
    internal_api: t("timeline_source_staff"),
    provider_webhook: t("timeline_source_network"),
    system: t("timeline_source_system"),
  };
  const statusLabel = (status: string) => {
    const key = RESCUE_STATUS_LABEL_KEYS[status];
    return key ? t(key) : status;
  };

  return (
    <ol className="space-y-0">
      {timeline.map((change, index) => {
        const isLast = index === timeline.length - 1;
        const actor = change.actor_user_id
          ? (actorLabels[change.actor_user_id] ?? shortId(change.actor_user_id))
          : null;
        const caption = [
          sourceLabel[change.source] ?? change.source,
          actor ? t("timeline_by", { actor }) : null,
        ]
          .filter(Boolean)
          .join(" · ");
        return (
          <li key={change.id} className="relative flex gap-3 pb-4 last:pb-0">
            {!isLast && (
              <span aria-hidden="true" className="absolute left-[3.5px] top-4 h-full w-px bg-border" />
            )}
            <span
              aria-hidden="true"
              className={cn(
                "mt-1.5 size-2 shrink-0 rounded-full",
                STATUS_DOT_CLASS[change.new_status] ?? "bg-muted-foreground",
                isLast && isNonTerminal && "animate-pulse",
              )}
            />
            <div className="min-w-0">
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-sm font-medium text-foreground">
                  {change.previous_status
                    ? `${statusLabel(change.previous_status)} → ${statusLabel(change.new_status)}`
                    : statusLabel(change.new_status)}
                </p>
                <p className="shrink-0 font-mono text-[11px] text-muted-foreground tabular-nums">
                  {formatDateTime(change.created_at)}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">{caption}</p>
              {change.reason && <p className="text-xs italic text-muted-foreground">“{change.reason}”</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
